import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import SearchableSelect from './SearchableSelect';

const BUTTON_VARIANTS = {
  primary: ({ loading }) => ({
    backgroundColor: loading
      ? UI_COLORS.BUTTONS.PRIMARY.loading.background
      : UI_COLORS.BUTTONS.PRIMARY.background,
    hoverBackgroundColor: loading
      ? UI_COLORS.BUTTONS.PRIMARY.loading.background
      : UI_COLORS.BUTTONS.PRIMARY.hover,
    textColor: loading
      ? UI_COLORS.BUTTONS.PRIMARY.loading.text
      : UI_COLORS.BUTTONS.PRIMARY.text,
    borderColor: UI_COLORS.BUTTONS.PRIMARY.border,
  }),
  secondary: ({ loading }) => ({
    backgroundColor: loading
      ? UI_COLORS.BUTTONS.SECONDARY.loading.background
      : UI_COLORS.BUTTONS.SECONDARY.background,
    hoverBackgroundColor: UI_COLORS.BUTTONS.SECONDARY.hover,
    textColor: loading
      ? UI_COLORS.BUTTONS.SECONDARY.loading.text
      : UI_COLORS.BUTTONS.SECONDARY.text,
    borderColor: UI_COLORS.BUTTONS.SECONDARY.border,
  }),
  danger: ({ loading }) => ({
    backgroundColor: loading
      ? UI_COLORS.BUTTONS.DANGER.loading.background
      : UI_COLORS.BUTTONS.DANGER.background,
    hoverBackgroundColor: UI_COLORS.BUTTONS.DANGER.hover.background,
    textColor: loading
      ? UI_COLORS.BUTTONS.DANGER.loading.text
      : UI_COLORS.BUTTONS.DANGER.text,
    borderColor: UI_COLORS.BUTTONS.DANGER.border || 'transparent',
  }),
  success: ({ loading }) => ({
    backgroundColor: loading
      ? UI_COLORS.BUTTONS.SUCCESS.loading.background
      : UI_COLORS.BUTTONS.SUCCESS.background,
    hoverBackgroundColor: UI_COLORS.BUTTONS.SUCCESS.hover.background,
    textColor: loading
      ? UI_COLORS.BUTTONS.SUCCESS.loading.text
      : UI_COLORS.BUTTONS.SUCCESS.text,
    borderColor: UI_COLORS.BUTTONS.SUCCESS.border || 'transparent',
  }),
  ghost: () => ({
    backgroundColor: 'transparent',
    hoverBackgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
    textColor: UI_COLORS.TEXT.PRIMARY,
    borderColor: UI_COLORS.BORDERS.MEDIUM,
  }),
  subtle: () => ({
    backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.BACKGROUND, 0.4),
    hoverBackgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.BACKGROUND, 0.6),
    textColor: UI_COLORS.TEXT.PRIMARY,
    borderColor: 'transparent',
  }),
};

/**
 * Resolve style configuration for a given button variant.
 *
 * This function looks up the variant resolver in BUTTON_VARIANTS and calls
 * it with the provided options (for example `loading`) to produce a
 * standardized style object used by toolbar buttons.
 *
 * Inputs:
 * - variant: string name of the variant (e.g. 'primary', 'ghost')
 * - options: object with runtime flags (e.g. { loading: true })
 *
 * Output: an object with backgroundColor, hoverBackgroundColor, textColor and borderColor.
 */
const resolveVariantStyles = (variant = 'primary', options = {}) => {
  const resolver = BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary;
  return resolver(options);
};

/**
 * ToolbarButton
 *
 * A small, theme-aware button used inside the ActionToolbar. Supports an
 * icon, optional label, loading/spinner state, hover tooltip and several
 * visual variants (primary, secondary, danger, ghost, etc.).
 *
 * Props (most are optional):
 * - icon: FontAwesome icon or string used to render the icon
 * - label: visible text label next to the icon
 * - onClick: click handler
 * - disabled: whether the button is disabled
 * - variant: visual variant key used to resolve colors
 * - showLabelBreakpoint: tailwind breakpoint class to hide/show label
 * - buttonStyle: override for inline styles
 * - tooltip: optional tooltip text (shown on hover)
 * - spinner/loading: shows a spinner on the icon when true
 *
 * Returns: a styled <button/> element.
 */
const ToolbarButton = ({
  icon,
  label,
  onClick,
  disabled = false,
  variant = 'primary',
  showLabelBreakpoint,
  className = '',
  buttonStyle,
  ariaLabel,
  tooltip,
  iconClassName = '',
  labelClassName = '',
  textColor,
  spinner = false,
  loading = false,
  hoverReveal = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Try to derive a human-friendly name from the FontAwesome icon object or string
  // This helper helps generate a default tooltip/title when none is provided.
  const getIconName = (icon) => {
    if (!icon) return '';
    // FontAwesome icon import objects usually have an `iconName` property
    if (typeof icon === 'object' && icon.iconName) return String(icon.iconName);
    // Sometimes icon may be passed as an array like ['fas', 'refresh']
    if (Array.isArray(icon) && icon[1]) return String(icon[1]);
    if (typeof icon === 'string') return icon;
    return '';
  };

  const variantStyles = useMemo(
    () => resolveVariantStyles(variant, { loading }),
    [variant, loading]
  );

  const {
    backgroundColor,
    hoverBackgroundColor,
    textColor: variantTextColor,
    borderColor,
  } = variantStyles;

  const computedTextColor = textColor || variantTextColor;

  const baseStyle = {
    backgroundColor: isHovered && !disabled ? hoverBackgroundColor : backgroundColor,
    color: computedTextColor,
    border: borderColor ? `2px solid ${borderColor}` : 'none',
    ...buttonStyle,
  };

  const labelClasses = [
    labelClassName,
    hoverReveal ? '' : (showLabelBreakpoint ? `hidden ${showLabelBreakpoint}:inline` : ''),
  ]
    .filter(Boolean)
    .join(' ');

  const computedTitle = tooltip || label || (() => {
    const n = getIconName(icon);
    if (!n) return '';
    // Convert dashed-names to Title Case
    return n.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
  })();

  // Use custom tooltip UI instead of native title for a styled hover tooltip.
  return (
    <button
      type="button"
      className={`px-4 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed select-none ${className}`}
      style={{ ...baseStyle, position: 'relative' }}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || computedTitle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span className="flex items-center justify-center gap-2">
        {icon && (
          <FontAwesomeIcon
            icon={icon}
            className={`${spinner ? 'animate-spin' : ''} ${showLabelBreakpoint ? '' : ''} ${iconClassName}`}
          />
        )}
        {label && (
          <span 
            className={labelClasses}
            style={hoverReveal ? {
              maxWidth: isHovered ? '200px' : '0px',
              opacity: isHovered ? 1 : 0,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              whiteSpace: 'nowrap',
              display: 'inline-block',
              verticalAlign: 'middle'
            } : {}}
          >
            {label}
          </span>
        )}
      </span>
      {/* Styled tooltip (custom) */}
      {isHovered && computedTitle && (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: UI_COLORS.BACKGROUNDS.CARD || '#111827',
            color: UI_COLORS.TEXT.PRIMARY || '#fff',
            padding: '6px 10px',
            borderRadius: 8,
            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
            fontSize: 12,
            whiteSpace: 'nowrap',
            zIndex: 60,
            border: `1px solid ${UI_COLORS.BORDERS.MEDIUM || 'rgba(255,255,255,0.08)'}`,
            opacity: 0.98,
          }}
        >
          <div style={{ padding: '0 2px' }}>{computedTitle}</div>
          {/* small arrow */}
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%) rotate(45deg)',
            width: 10,
            height: 10,
            backgroundColor: UI_COLORS.BACKGROUNDS.CARD || '#111827',
            borderLeft: `1px solid ${UI_COLORS.BORDERS.MEDIUM || 'rgba(255,255,255,0.08)'}`,
            borderTop: `1px solid ${UI_COLORS.BORDERS.MEDIUM || 'rgba(255,255,255,0.08)'}`,
          }} />
        </div>
      )}
    </button>
  );
};

/**
 * ActionToolbar
 *
 * A flexible toolbar that can render a search box, filters and action
 * buttons. The component normalizes the passed `filters` and `actions`
 * arrays and provides consistent layout, theming and accessibility.
 *
 * Props:
 * - showSearch, searchValue, onSearchChange: search input control
 * - filters: an array of React nodes to render as filter controls
 * - actions: an array of either React elements or action config objects
 * - containerProps / className / style: layout customization
 *
 * Returns: a responsive toolbar UI with optional search and action items.
 */
const ActionToolbar = ({
  showSearch = true,
  searchValue = '',
  onSearchChange,
  searchPlaceholder,
  searchAriaLabel,
  searchDisabled = false,
  searchInputProps = {},
  filters = [],
  actions = [],
  className = '',
  style,
  containerProps = {},
}) => {
  const { t } = useTranslation();
  const effectiveSearchPlaceholder = searchPlaceholder ?? t('common.searchPlaceholder');
  const effectiveSearchAriaLabel = searchAriaLabel ?? t('common.searchAriaLabel');

  const normalizedFilters = useMemo(
    () =>
      (filters || [])
        .filter(Boolean)
        .map((filter, index) => {
          // If it's already a React element, render it directly
          if (React.isValidElement(filter)) {
            return (<div key={`filter-${index}`} className="flex-shrink-0">{filter}</div>);
          }

          // If a descriptor object is provided, render a control based on its type
          if (filter && typeof filter === 'object') {
            const key = filter.key || `filter-${index}`;
            const type = filter.type || 'select';
            const label = filter.label || '';
            const value = filter.value;
            const options = Array.isArray(filter.options) ? filter.options : [];
            const onChange = filter.onChange;

            if (type === 'select') {
              return (
                <div key={key} className="flex-shrink-0">
                  <label className="text-sm mr-2" style={{ color: UI_COLORS.FORMS.LABEL }}>{label}</label>
                  <select
                    value={value}
                    onChange={(e) => onChange && onChange(e.target.value)}
                    className="px-3 py-2 rounded-xl border-2"
                    style={{
                      backgroundColor: UI_COLORS.FORMS.INPUT.background,
                      borderColor: UI_COLORS.FORMS.INPUT.border,
                      color: UI_COLORS.TEXT.BLACK || UI_COLORS.FORMS.INPUT.text,
                    }}
                    aria-label={label}
                  >
                    {options.map(opt => (
                      <option
                        key={opt.value}
                        value={opt.value}
                        style={{ color: UI_COLORS.TEXT.BLACK || UI_COLORS.FORMS.INPUT.text }}
                      >
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }

            if (type === 'multiselect') {
              // Multi-select filter (e.g. Sub Categories). Empty selection = "all".
              return (
                <div key={key} className="flex-shrink-0 flex items-center gap-2">
                  <label className="text-sm" style={{ color: UI_COLORS.FORMS.LABEL }}>{label}</label>
                  <div style={{ minWidth: filter.minWidth || 200 }}>
                    <SearchableSelect
                      isMulti
                      options={options}
                      value={Array.isArray(value) ? value : []}
                      onChange={(e) => onChange && onChange(e.target.value)}
                      placeholder={filter.placeholder || t('warehouseInventory.actions.filters.all')}
                      loading={!!filter.loading}
                    />
                  </div>
                </div>
              );
            }

            if (type === 'text' || type === 'input' || type === 'date') {
              return (
                <div key={key} className="flex-shrink-0">
                  <label className="text-sm mr-2" style={{ color: UI_COLORS.FORMS.LABEL }}>{label}</label>
                  <input
                    type={type === 'date' ? 'date' : 'text'}
                    value={value || ''}
                    onChange={(e) => onChange && onChange(e.target.value)}
                    placeholder={filter.placeholder || label}
                    className="px-3 py-2 rounded-xl border-2"
                    style={{
                      backgroundColor: UI_COLORS.FORMS.INPUT.background,
                      borderColor: UI_COLORS.FORMS.INPUT.border,
                      color: UI_COLORS.TEXT.BLACK || UI_COLORS.FORMS.INPUT.text,
                    }}
                    aria-label={label}
                  />
                </div>
              );
            }
          }

          // Fallback: stringify
          return (
            <div key={`filter-${index}`} className="flex-shrink-0">
              {String(filter)}
            </div>
          );
        }),
    [filters, t]
  );

  const normalizedActions = useMemo(
    () =>
      (actions || [])
        .filter(Boolean)
        .map((action, index) => {
          if (React.isValidElement(action)) {
            return <React.Fragment key={`action-${index}`}>{action}</React.Fragment>;
          }

          const { key, ...rest } = action;
          return <ToolbarButton key={key || `action-${index}`} {...rest} />;
        }),
    [actions]
  );

  const hasSearch = showSearch;
  const hasFilters = normalizedFilters.length > 0;
  const hasActions = normalizedActions.length > 0;

  return (
    <div
      className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-6 bg-white rounded-2xl shadow-lg ${className}`}
      style={style}
      {...containerProps}
    >
      <div className="flex flex-col gap-4 w-full">
        {(hasSearch || hasActions) && (
          <div className="flex flex-wrap items-center gap-3 justify-between">
            {hasSearch && (
              <div className="relative flex-1 w-full min-w-0 sm:min-w-[220px] max-w-3xl">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder={effectiveSearchPlaceholder}
                  value={searchValue}
                  onChange={(event) => onSearchChange?.(event.target.value)}
                  aria-label={effectiveSearchAriaLabel}
                  disabled={searchDisabled}
                  className="w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                  style={{
                    backgroundColor: UI_COLORS.FORMS.INPUT.background,
                    borderColor: UI_COLORS.FORMS.INPUT.border,
                    color: UI_COLORS.FORMS.INPUT.text,
                    '--tw-ring-color': colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.3),
                  }}
                  {...searchInputProps}
                />
              </div>
            )}
            {hasActions && (
              <div className="flex flex-wrap items-center gap-3 justify-end flex-shrink-0 w-full sm:w-auto">
                {normalizedActions}
              </div>
            )}
          </div>
        )}

        {hasFilters && (
          <div className="flex flex-wrap items-center gap-3 pt-1 sm:pt-2">
            {normalizedFilters}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionToolbar;
